/* eslint-disable max-lines-per-function */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import { Grid } from "@mui/material";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import {
  newUserViewer,
  newUserViewer_viewer_lists as listType,
  updateNewUserForTeam,
  updateNewUserForTeamVariables,
} from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { Card } from "components/haven-design";
import { BackToLogin, HavenConnectLoginLogo, LoginCardSubtitle, LoginCardTitle, LoneGrid } from "components/login/components/shared-components";
import { NewUserForm, InitialUserData } from "./new-user-form";

const QUERY = gql`
  query newUserViewer {
    viewer {
      id
      firstName
      lastName
      organizationId
      email
      roles
      directPhone
      title
      lists {
        id
        name
      }
    }
  }
`;

const UPDATE_USER_MUTATION = gql`
  mutation updateNewUserForTeam($id: ID!, $data: UserInput!) {
    updateUser(id: $id, data: $data) {
      id
    }
  }
`;

export const NewUserUpdate: React.FC = () => {
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { loading, error, data } = useQuery<newUserViewer>(QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [updateUser, updateUserMutation] = useMutation<
  updateNewUserForTeam,
  updateNewUserForTeamVariables
  >(UPDATE_USER_MUTATION);

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;
  if (!data.viewer) return <ServerError desc="Missing Data" />;
  const viewer = data.viewer as NonNullable<newUserViewer["viewer"]>;

  const updateUserInfo = (values: InitialUserData) => {
    const requiredParams = {
      organizationId: viewer.organizationId,
      roles: viewer.roles,
      email: viewer.email,
      lists: ((data?.viewer?.lists || []) as listType[])
        .map(({ id, name }) => ({
          id,
          name,
        })),
    };
    const filteredValues = { ...values };
    delete filteredValues.terms;

    updateUser({
      variables: {
        id: viewer.id || "",
        data: {
          ...filteredValues,
          ...requiredParams,
        },
      },
    })
      .then(() => {
        navigate("/");
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Unable to update your profile. Contact support@havenconnect.com.", status: "error" });
        throw new Error(err);
      });
  };
  const nationalPhone = viewer.directPhone ? viewer.directPhone.replace("+1", "") : "";

  const initialUserData = {
    firstName: viewer.firstName || "",
    lastName: viewer.lastName || "",
    title: viewer.title || "",
    directPhone: nationalPhone,
    terms: false,
  };

  return (
    <Grid container spacing={2} justifyContent="center">
      <Grid size={{ xs: 12 }}>
        <HavenConnectLoginLogo />
      </Grid>
      <Card>
        <LoneGrid>
          <LoginCardTitle title="Tell us about yourself!" />
        </LoneGrid>
        <LoneGrid>
          <LoginCardSubtitle>
            We will use this information to personalize Haven Connect for you.
          </LoginCardSubtitle>
        </LoneGrid>

        <NewUserForm
          isSending={updateUserMutation.loading}
          initialData={initialUserData}
          onSubmit={updateUserInfo}
        />
        <BackToLogin />
      </Card>
    </Grid>
  );
};
